import React from 'react';
import './ChartCard.css';

/** 部品の状態（pages/PerformanceDashboard/useDashboardData.js の SECTION_STATUS はこれを使う） */
export const CHART_CARD_STATUS = {
    LOADING: 'loading',
    READY: 'ready',
    FORBIDDEN: 'forbidden',
    ERROR: 'error',
};

/** 値の表を開くボタンの既定の文言（表が 1 つのカード） */
const DEFAULT_TABLE_TOGGLE_LABEL = '値の表を開く';

/**
 * グラフのカード（SCR-P-001 DP-P-005〜008・012。コンポーネント対応表 §4.2）
 *
 * グラフの下に要約の文と、開閉できる値の表を置く（DP-P-012。グラフを読めない人にも同じ内容を渡すため。§7.2）。
 * 取得の状態ごとの表示（スケルトン・権限外・取得失敗）もここで出す（§6.1・§6.3。失敗はカードごとに出す）。
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.status - CHART_CARD_STATUS
 * @param {{forbidden: string, failed: string, reload: string}} props.messages
 * @param {() => void} [props.onReload] - 取得に失敗したときの「再読み込み」
 * @param {string} [props.summary] - 要約の文
 * @param {Array<{caption: string, columns: string[], rows: Array<Array<string>>, toggleLabel?: string}>} [props.tables]
 *   - 値の表（1 枚のカードに 2 つのグラフがあるときは 2 つ）。toggleLabel は開くボタンの文言で、無ければ「値の表を開く」。
 *     表が 2 つのカードでは、どちらの表かが分かる文言を渡す（同じ文言が 2 つ続くと、読み上げでも区別できない。SCR-P-001 §6.5 #28）
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children - グラフ
 */
function ChartCard({ title, status, messages, onReload, summary, tables, className, children }) {
    return (
        <section className={`chart-card${className ? ` ${className}` : ''}`} aria-label={title}>
            <h2 className="chart-card-title">{title}</h2>
            <ChartCardBody
                status={status}
                messages={messages}
                onReload={onReload}
                summary={summary}
                tables={tables}
            >
                {children}
            </ChartCardBody>
        </section>
    );
}

function ChartCardBody({ status, messages, onReload, summary, tables = [], children }) {
    if (status === CHART_CARD_STATUS.LOADING) {
        return (
            <div className="chart-card-skeleton skeleton-fade-in" role="status" aria-label="読み込み中">
                <div className="skeleton-line chart-card-skeleton-plot" />
                <div className="skeleton-line chart-card-skeleton-text" />
            </div>
        );
    }
    if (status === CHART_CARD_STATUS.FORBIDDEN) {
        return <p className="chart-card-message" role="alert">{messages.forbidden}</p>;
    }
    if (status === CHART_CARD_STATUS.ERROR) {
        return (
            <div className="chart-card-message" role="alert">
                <p>{messages.failed}</p>
                {onReload && (
                    <button type="button" className="chart-card-reload" onClick={onReload}>
                        {messages.reload}
                    </button>
                )}
            </div>
        );
    }
    return (
        <>
            <div className="chart-card-plot">{children}</div>
            {summary && <p className="chart-card-summary">{summary}</p>}
            {tables.map((table) => <ChartDataTable key={table.caption} table={table} />)}
        </>
    );
}

function ChartDataTable({ table }) {
    return (
        <details className="chart-card-table">
            <summary>{table.toggleLabel ?? DEFAULT_TABLE_TOGGLE_LABEL}</summary>
            <div className="chart-card-table-scroll">
                <table>
                    <caption>{table.caption}</caption>
                    <thead>
                        <tr>
                            {table.columns.map((col) => <th key={col} scope="col">{col}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {table.rows.map((row, i) => (
                            // 行の並びは応答の並びのままで、行の追加・並び替えをしないため添字を key にする
                            <tr key={i}>
                                {row.map((cell, j) => (j === 0
                                    ? <th key={j} scope="row">{cell}</th>
                                    : <td key={j}>{cell}</td>))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </details>
    );
}

export default ChartCard;
